import React from 'react';
import dynamic from 'next/dynamic';
import ReactPlayer from 'react-player/lazy';
import createCache from '@emotion/cache';
import { TssCacheProvider } from 'tss-react';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import Head from 'next/head';
import { END } from 'redux-saga';
import { useTracking } from 'react-tracking';
import { SnackbarProvider } from 'notistack';
import { DefaultSeo, OrganizationJsonLd } from 'next-seo';
import { useSelector } from 'react-redux';
import { find, get } from 'lodash';
import { useRouter, Router } from 'next/router';
import { ReactQueryDevtools } from 'react-query/devtools';
import NProgress from 'nprogress';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/styles.scss';
import 'react-day-picker/lib/style.css';
import { QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { appWithTranslation, i18n } from '../src/i18n';
import { getCountriesAction, getUserConstants, setLanguageAction } from '../store/actions';
import { updateLoginAction } from '../utils/auth';
import { getBugsnagPlugin, isBugsnagInitialized } from '../utils/bugsnagClientWrapper';
import { selectRegistrations } from '../containers/Authentification/Registration/selectors';
import { clearIntervalForPing, setIntervalForPing, updateChurnedStatusAction } from '../utils/common';
import usePermissions from '../utils/permissions';
import { selectListData, selectIsLoggedInState } from '../store/selectors';
import {
  selectAppDomain,
  selectUserData,
  selectActiveProfile,
  selectUserProfiles,
  selectActiveProfileSubscriptions,
} from '../containers/App/selectors';
import { initialState } from '../containers/App/reducer';
import { pingSegment, trackUserAction, userIdentifyEvent, trackLoggedIn, trackEvent } from '../utils/tracking';
import { trackUserActivityEvents } from '../utils/userActivityEvents';
import { newQueryClient } from '../utils/queriesUtil';
import PageScrollProvider from '../utils/hooks/usePageScroll';
import theme from '../src/theme';
import { wrapper } from '../store/store';
import 'nprogress/nprogress.css';
import useInterval from '../utils/hooks/useInterval';
import { getCookie, removeCookie } from '../utils/cookie';
import { SOCIAL_LOGIN } from '../constants/cookieConstants';
import { allLanguageCodes } from '../constants/languageCodes';
import NotificationPopupProvider from '../utils/hooks/useNotificationPopup';
import NotificationBannerProvider from '../utils/hooks/useNotificationBanner';
import DialogsProvider from '../utils/hooks/useDialogs';
import '../src/loadlocale';
import VimeoOTTPlayer from '../utils/vimeoOttPlayer';
import config from '../config/config.json';
import Blitzer from '@truelinked/blitzer';
import ReactGA from 'react-ga';
import { trackPageView } from '../utils/blitzerTracking';

let blitzer;
if (process.env.NEXT_PUBLIC_IS_INSIGHTS_TRACKING_ENABLED) {
  blitzer = new Blitzer({
    frontbaseUrl: config.FRONTBASE_URL,
    blitzerAuthCookie: 'x-blitzer-authorization',
    env: config.APP_ENV,
  });
}

ReactGA.initialize(process.env.NEXT_PUBLIC_GA2_TRACKID, {
  titleCase: false,
});

ReactPlayer.addCustomPlayer(VimeoOTTPlayer);

dayjs.extend(localeData);
// dynamic imports
const CssBaseline = dynamic(() => import('@mui/material/CssBaseline'));
const Page = dynamic(() => import('../components/Page'));
const Error = dynamic(() => import('./_error'));
const Notifier = dynamic(() => import('../components/Notifier'));
const OBAppContainer = dynamic(() => import('../containers/App'));
const SEO = dynamic(() => import('../utils/seo'));

const baseURL = process.env.FRONTBASE_URL;

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

// Client-side cache, shared for the whole session of the user in the browser.
let muiCache;
let tssStylesCache;
// Client-side cache, shared for the whole session of the user in the browser.
/* eslint-disable */
export const createMuiCache = () =>
  (muiCache = createCache({
    key: 'mui-cache',
    prepend: true,
  }));
export const createTssCache = () =>
  (tssStylesCache = createCache({
    key: 'tss-cache',
    prepend: false,
  }));
/* eslint-enable */

const renderAppContainer = (Component, pageProps, emotionCache, tssCache) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryClientRef = React.useRef();
  if (!queryClientRef.current) {
    queryClientRef.current = newQueryClient();
  }

  return (
    <NotificationBannerProvider>
      <NotificationPopupProvider>
        <DialogsProvider>
          <PageScrollProvider>
            <Page>
              <Head>
                <meta
                  name="viewport"
                  content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no,shrink-to-fit=no"
                />
              </Head>
              <DefaultSeo {...SEO} />
              <OrganizationJsonLd
                id="https://www.operabase.com/en"
                type="Organization"
                name="Operabase"
                legalName="Arts Consolidated Aps"
                url="https://www.operabase.com/en"
                logo="https://www.operabase.com/images/operabase-logo-large.png"
                image="https://www.operabase.com/images/operabase-logo-large.png"
                email="contact@operabase.com"
                telephone="-"
                address={{
                  addressLocality: 'København, Denmark',
                  streetAddress: 'Lyngbyvej 20',
                  postalCode: '2100',
                  addressCountry: 'DK',
                }}
                sameAs={[
                  'https://www.facebook.com/operabase/',
                  'https://twitter.com/BaseOpera',
                  'https://en.wikipedia.org/wiki/Operabase',
                  'https://www.linkedin.com/company/operabase-global',
                  'https://www.instagram.com/operabaseglobal/',
                ]}
                foundingDate="1996"
              />

              <TssCacheProvider value={tssCache ?? tssStylesCache ?? createTssCache()}>
                <CacheProvider value={emotionCache ?? muiCache ?? createMuiCache()} injectFirst>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <SnackbarProvider maxSnack={3}>
                      <Notifier />
                      <QueryClientProvider client={queryClientRef.current}>
                        <Hydrate state={pageProps.dehydratedState}>
                          <OBAppContainer Component={Component} pageProps={pageProps} />
                          <ReactQueryDevtools initialIsOpen={false} />
                        </Hydrate>
                      </QueryClientProvider>
                    </SnackbarProvider>
                  </ThemeProvider>
                </CacheProvider>
              </TssCacheProvider>
            </Page>
          </PageScrollProvider>
        </DialogsProvider>
      </NotificationPopupProvider>
    </NotificationBannerProvider>
  );
};

const addDayjsLocale = locale => {
  // check if datetime locale override is present
  const selectedAppLanguage = allLanguageCodes.find(o => o.iso === locale);
  const dateTimeLocale = get(selectedAppLanguage, 'datetimeLocale', locale);

  dayjs.locale(dateTimeLocale);
};

let router = '';
let [activeProfile, profiles, registrations, userDetails, activeSubscription] = [{}, [], [], {}, {}];
const PING_SEGMENT_INTERVAL = 300000;
function OBApp({ Component, pageProps, emotionCache, tssCache }) {
  // You can now get a ref directly to the DOM button:

  const { Track, trackEvent } = useTracking(
    { app: 'Frontbase' },
    {
      dispatch: payload => {
        dispatchFunc(payload);
      },
    },
  );
  const { isTranslator } = usePermissions();
  router = useRouter();
  const settings = useSelector(selectListData(selectAppDomain, initialState.settings.listName));
  const contextEditor = get(find(settings, { identifier: 'context_editor' }), 'value') === 'true';
  const isLoggedIn = useSelector(selectIsLoggedInState());
  userDetails = useSelector(selectUserData(selectAppDomain));
  activeProfile = useSelector(selectActiveProfile(selectAppDomain));
  profiles = useSelector(selectUserProfiles());
  registrations = useSelector(selectRegistrations());
  activeSubscription = useSelector(selectActiveProfileSubscriptions());

  const profileInfo = {
    ...activeProfile,
    allProfileWithAccess: profiles.length,
  };

  const onFocus = () => {
    if (isLoggedIn) {
      setIntervalForPing();
    }
  };

  const onBlur = () => {
    clearIntervalForPing();
  };

  const onCopy = e => {
    trackEvent(
      trackEvent({
        name: 'copy',
        data: {
          content: e?.target?.textContent,
        },
      }),
    );
  };

  React.useEffect(() => {
    trackUserActivityEvents();
    // only on client
    if (isLoggedIn) {
      userIdentifyEvent({ ...userDetails, profileId: activeProfile.id, profileInfo });
    }

    const isSocialLogin = getCookie(SOCIAL_LOGIN);
    if (isSocialLogin) {
      trackEvent(
        trackLoggedIn({
          login_type: 'social_login',
          email: userDetails?.email,
        }),
      );
      removeCookie(SOCIAL_LOGIN);
    }

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    window.addEventListener('copy', onCopy);
    onFocus();
    if (typeof window !== 'undefined') {
      const pagePath = router.asPath;
      ReactGA.pageview(pagePath);
      trackEvent(trackPageView({}));
    }
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('copy', onCopy);
    };
  }, []);

  React.useEffect(() => {
    if (isTranslator) {
      document.dispatchEvent(new Event('lokalise-update-elements'));
    }
  }, [contextEditor, isTranslator]);

  const trackSegment = () => {
    trackEvent(pingSegment());
  };
  useInterval(trackSegment, PING_SEGMENT_INTERVAL);

  // Cannot use effect as should wait for locale before render
  const locale = i18n?.language?.toLowerCase();
  if (locale && locale !== dayjs.locale() && locale !== 'keys') {
    addDayjsLocale(locale);
  }

  if (isBugsnagInitialized()) {
    const ErrorBoundary = getBugsnagPlugin();
    return (
      <Track>
        <ErrorBoundary FallbackComponent={Error}>
          {renderAppContainer(Component, pageProps, emotionCache, tssCache)}
        </ErrorBoundary>
      </Track>
    );
  }
  return <Track>{renderAppContainer(Component, pageProps, emotionCache, tssCache)}</Track>;
}

OBApp.getInitialProps = async ({ Component, ctx }) => {
  // set the logged-in state
  const { store } = ctx;
  const state = store.getState();
  const locale = i18n?.language?.toLowerCase() || ctx?.req?.language;
  if (locale && locale !== dayjs.locale() && locale !== 'keys') {
    addDayjsLocale(locale);
  }

  const { countries, userConstants } = state.global;

  // set loggedin state && fetch user info
  await updateLoginAction(ctx);
  updateChurnedStatusAction(ctx);

  if (!userConstants.length) {
    store.dispatch(getUserConstants());
  }

  if (locale) {
    store.dispatch(setLanguageAction(locale));
  }

  const pageProps = {
    ...(Component.getInitialProps ? await Component.getInitialProps(ctx) : {}),
  };

  if (ctx.req) {
    ctx.store.dispatch(END);
    await ctx.store.sagaTask.toPromise();
  }

  if (countries.length === 0) {
    store.dispatch(getCountriesAction());
  }

  return {
    pageProps: {
      ...pageProps,
    },
  };
};

export function reportWebVitals(metric) {
  if (process.env.NODE_ENV === 'development') {
    console.log(metric);
  }
}

// custom dispatch in addition to pushing segment
const dispatchFunc = payload => {
  const profile = {
    ...activeProfile,
    allProfileWithAccess: profiles.length,
    registrationCompleteDate: registrations.find(r => r?.profile?.id === activeProfile.id)?.completed_date,
  };

  const data = trackUserAction({ payload, router, profile, userDetails, activeSubscription });

  if (blitzer && data.trackingType === 'insights') {
    blitzer.capture(data);
    return;
  }

  if (typeof window !== 'undefined') {
    ReactGA.event({
      category: 'All',
      action: data?.name,
    });
    // Google TagManager Tracking
    window.dataLayer?.push({ event: data.name, ...data });
    if (window.Appcues) {
      window.Appcues.track(data.name, data);
    }
  }
};

export default wrapper.withRedux(appWithTranslation(OBApp));
